/**
 * Image processing utilities for cropping, resizing, and optimizing images
 */

export interface CropArea {
    x: number;
    y: number;
    width: number;
    height: number;
}

export type ImageOutputFormat = 'image/jpeg' | 'image/png' | 'image/webp';

export interface ProcessImageOptions {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    outputFormat?: ImageOutputFormat;
}

const FORMAT_EXTENSIONS: Record<ImageOutputFormat, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
};

/**
 * Keep formats that support transparency. JPEG cannot, so PNG/GIF/WebP
 * must not be converted to JPEG or transparent pixels become black.
 */
export const getImageOutputFormat = (file: File | string): ImageOutputFormat => {
    const mimeType = typeof file === 'string' ? '' : file.type;
    const source = typeof file === 'string' ? file : `${file.name} ${mimeType}`;

    if (
        mimeType === 'image/png' ||
        mimeType === 'image/gif' ||
        /\.png(\?|$)/i.test(source) ||
        /\.gif(\?|$)/i.test(source)
    ) {
        return 'image/png';
    }

    if (mimeType === 'image/webp' || /\.webp(\?|$)/i.test(source)) {
        return 'image/webp';
    }

    return 'image/jpeg';
};

export const getImageFileName = (
    originalName: string,
    format: ImageOutputFormat
): string => {
    const baseName = originalName.replace(/\.[^/.]+$/, '') || 'image';
    return `${baseName}.${FORMAT_EXTENSIONS[format]}`;
};

/**
 * Create an image element from a file or URL
 */
export const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

/**
 * Convert degrees to radians
 */
export const getRadianAngle = (degreeValue: number): number => {
    return (degreeValue * Math.PI) / 180;
};

/**
 * Returns the new bounding area of a rotated rectangle
 */
export const rotateSize = (width: number, height: number, rotation: number) => {
    const rotRad = getRadianAngle(rotation);

    return {
        width:
            Math.abs(Math.cos(rotRad) * width) +
            Math.abs(Math.sin(rotRad) * height),
        height:
            Math.abs(Math.sin(rotRad) * width) +
            Math.abs(Math.cos(rotRad) * height),
    };
};

/**
 * Crop and optionally resize an image
 */
export const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: CropArea,
    rotation = 0,
    flip = { horizontal: false, vertical: false },
    options: ProcessImageOptions = {}
): Promise<{ file: File; url: string }> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('No 2d context');
    }

    const rotRad = getRadianAngle(rotation);

    // Calculate bounding box of the rotated image
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
        image.width,
        image.height,
        rotation
    );

    // Set canvas size to match the bounding box
    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    // Translate canvas context to a central location to allow rotating and flipping around the center
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.translate(-image.width / 2, -image.height / 2);

    // Draw rotated image
    ctx.drawImage(image, 0, 0);

    const croppedCanvas = document.createElement('canvas');
    const croppedCtx = croppedCanvas.getContext('2d');

    if (!croppedCtx) {
        throw new Error('No 2d context');
    }

    // Set the size of the cropped canvas
    croppedCanvas.width = pixelCrop.width;
    croppedCanvas.height = pixelCrop.height;

    // Draw the cropped image onto the new canvas
    croppedCtx.drawImage(
        canvas,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    // Optimize and resize if needed
    const finalCanvas = await optimizeImage(
        croppedCanvas,
        options.maxWidth,
        options.maxHeight
    );

    // Convert to blob
    return new Promise((resolve, reject) => {
        finalCanvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                    return;
                }

                const outputFormat = options.outputFormat || 'image/jpeg';
                const file = new File(
                    [blob],
                    getImageFileName('cropped-image', outputFormat),
                    {
                        type: outputFormat,
                        lastModified: Date.now(),
                    }
                );

                const url = URL.createObjectURL(blob);
                resolve({ file, url });
            },
            options.outputFormat || 'image/jpeg',
            options.quality || 0.9
        );
    });
};

/**
 * Optimize image by resizing while maintaining aspect ratio
 */
export const optimizeImage = async (
    canvas: HTMLCanvasElement,
    maxWidth = 1920,
    maxHeight = 1080
): Promise<HTMLCanvasElement> => {
    const { width, height } = canvas;

    // If image is smaller than max dimensions, return as is
    if (width <= maxWidth && height <= maxHeight) {
        return canvas;
    }

    // Calculate new dimensions while maintaining aspect ratio
    let newWidth = width;
    let newHeight = height;

    if (width > maxWidth) {
        newWidth = maxWidth;
        newHeight = (height * maxWidth) / width;
    }

    if (newHeight > maxHeight) {
        newHeight = maxHeight;
        newWidth = (width * maxHeight) / height;
    }

    // Create new canvas with optimized dimensions
    const optimizedCanvas = document.createElement('canvas');
    const ctx = optimizedCanvas.getContext('2d');

    if (!ctx) {
        throw new Error('No 2d context');
    }

    optimizedCanvas.width = newWidth;
    optimizedCanvas.height = newHeight;

    // Draw image with new dimensions
    ctx.drawImage(canvas, 0, 0, newWidth, newHeight);

    return optimizedCanvas;
};

/**
 * Compress image file
 */
export const compressImage = async (
    file: File,
    options: ProcessImageOptions = {}
): Promise<{ file: File; url: string }> => {
    const imageSrc = URL.createObjectURL(file);
    const image = await createImage(imageSrc);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('No 2d context');
    }

    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);

    // Optimize
    const finalCanvas = await optimizeImage(
        canvas,
        options.maxWidth,
        options.maxHeight
    );

    return new Promise((resolve, reject) => {
        finalCanvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                    return;
                }

                const outputFormat =
                    options.outputFormat ||
                    getImageOutputFormat(file);
                const compressedFile = new File(
                    [blob],
                    getImageFileName(file.name, outputFormat),
                    {
                        type: outputFormat,
                        lastModified: Date.now(),
                    }
                );

                const url = URL.createObjectURL(blob);
                URL.revokeObjectURL(imageSrc);
                resolve({ file: compressedFile, url });
            },
            options.outputFormat || getImageOutputFormat(file),
            options.quality || 0.9
        );
    });
};

/**
 * Validate if file is an image
 */
export const isImageFile = (file: File | string): boolean => {
    if (typeof file === 'string') {
        return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(file);
    }
    return file.type.startsWith('image/');
};

/**
 * Validate if file is a PDF
 */
export const isPdfFile = (file: File | string): boolean => {
    if (typeof file === 'string') {
        return /\.pdf$/i.test(file);
    }
    return file.type === 'application/pdf';
};

/**
 * Get file size in human-readable format
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};
